import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiAvatarComponent } from '@ui';
import { chatUnreadFeature } from '@share-chat';
import { AuthActions } from '../../store/state/auth/auth.actions';
import { organizationFeature } from '../../store/state/organization/organization.feature';
import { isOrganizationChatAvailable } from '../../store/state/organization/organization.selectors';
import { UserMenuComponent } from '../user-menu/user-menu.component';

const MIN_SWITCHABLE_ORGANIZATIONS_FOR_SEARCH = 5;

@Component({
  selector: 'app-organization-menu',
  imports: [MatTooltip, TranslatePipe, UiAvatarComponent, UserMenuComponent],
  templateUrl: './organization-menu.component.html',
  styleUrl: './organization-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationMenuComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly elementRef = inject(ElementRef) as ElementRef<HTMLElement>;
  private readonly translate = inject(TranslateService);

  protected readonly organization = this.store.selectSignal(
    organizationFeature.selectActiveOrganization,
  );
  protected readonly organizations = this.store.selectSignal(
    organizationFeature.selectUserOrganizations,
  );
  private readonly unreadBySpace = this.store.selectSignal(
    chatUnreadFeature.selectSpaceActivityTotals,
  );
  protected readonly selectorOpen = signal(false);
  private readonly organizationSwitching = this.store.selectSignal(
    organizationFeature.selectSwitching,
  );
  private readonly switchTargetOrganizationId = this.store.selectSignal(
    organizationFeature.selectSwitchTargetOrganizationId,
  );
  protected readonly switchInProgress = computed(() =>
    this.organizationSwitching(),
  );
  protected readonly searchQuery = signal('');
  protected readonly hasHiddenOrganizations = signal(false);
  protected readonly visibleCompactOrganizations = signal(
    Number.MAX_SAFE_INTEGER,
  );
  protected readonly hiddenOrganizationsUnreadTotal = computed(() =>
    this.shortcutOrganizations()
      .slice(this.visibleCompactOrganizations())
      .reduce(
        (total, organization) =>
          total + this.organizationUnreadCount(organization.synapseSpaceId),
        0,
      ),
  );

  private organizationList?: HTMLElement;
  private organizationRail?: HTMLElement;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  @ViewChild('organizationList')
  protected set organizationListElement(
    element: ElementRef<HTMLElement> | undefined,
  ) {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.organizationList = element?.nativeElement;
    this.organizationRail = this.organizationList?.parentElement ?? undefined;

    if (!this.organizationList) return;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() =>
        this.updateHiddenOrganizations(),
      );
      this.resizeObserver.observe(this.organizationList);
      if (this.organizationRail) {
        this.resizeObserver.observe(this.organizationRail);
      }
    }

    this.mutationObserver = new MutationObserver(() =>
      this.updateHiddenOrganizations(),
    );
    this.mutationObserver.observe(this.organizationList, {
      childList: true,
      subtree: true,
    });
    queueMicrotask(() => this.updateHiddenOrganizations());
  }

  protected readonly activeOrganizationName = computed(() => {
    const active = this.organization();
    return (
      active?.settings?.orgName ??
      this.organizations().find(
        (item) =>
          item.id === (active?._id ?? this.switchTargetOrganizationId()),
      )?.name ??
      ''
    );
  });

  protected readonly activeOrganizationColor = computed(() => {
    const active = this.organization();
    return (
      active?.settings?.orgColor ??
      this.organizations().find(
        (item) =>
          item.id === (active?._id ?? this.switchTargetOrganizationId()),
      )?.color
    );
  });

  protected readonly hasMultipleOrganizations = computed(
    () => this.shortcutOrganizations().length > 0,
  );

  protected readonly showOrganizationSearch = computed(
    () =>
      this.shortcutOrganizations().length >=
      MIN_SWITCHABLE_ORGANIZATIONS_FOR_SEARCH,
  );

  protected readonly shortcutOrganizations = computed(() => {
    const displayedOrganizationId =
      this.organization()?._id ?? this.switchTargetOrganizationId();
    const organizationsWithChat = this.organizations().filter(
      isOrganizationChatAvailable,
    );
    return organizationsWithChat.filter(
      (item) => item.id !== displayedOrganizationId,
    );
  });

  @Output() public readonly selectorOpenChange = new EventEmitter<boolean>();

  protected toggleSelector(): void {
    if (!this.hasMultipleOrganizations()) return;
    if (this.selectorOpen()) {
      this.closeSelector();
      return;
    }

    this.searchQuery.set('');
    this.selectorOpen.set(true);
    this.selectorOpenChange.emit(true);
    this.updateHiddenOrganizations();
  }

  protected openSelector(): void {
    if (!this.selectorOpen()) this.toggleSelector();
  }

  public closeSelector(): void {
    this.selectorOpen.set(false);
    this.searchQuery.set('');
    this.selectorOpenChange.emit(false);
    queueMicrotask(() => this.updateHiddenOrganizations());
  }

  protected updateSearch(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  protected matchesSearch(organizationName: string): boolean {
    const query = this.searchQuery().trim().toLocaleLowerCase();
    return !query || organizationName.toLocaleLowerCase().includes(query);
  }

  protected organizationUnreadCount(spaceId: string | undefined): number {
    return spaceId ? (this.unreadBySpace()[spaceId] ?? 0) : 0;
  }

  protected unreadBadgeLabel(spaceId: string | undefined): string {
    const count = this.organizationUnreadCount(spaceId);
    return count > 99 ? '99+' : count > 0 ? String(count) : '';
  }

  protected organizationAriaLabel(
    organizationName: string,
    spaceId: string | undefined,
  ): string {
    const unreadCount = this.organizationUnreadCount(spaceId);
    if (unreadCount <= 0) return organizationName;
    const unreadLabel = String(
      this.translate.instant('CHAT.SIDEBAR_UNREAD_BADGE_ARIA', {
        count: unreadCount,
      }),
    );
    return `${organizationName}. ${unreadLabel}`;
  }

  protected hiddenOrganizationsAriaLabel(): string {
    const label = String(this.translate.instant('SIDEBAR.SHOW_ORGANIZATIONS'));
    const unreadCount = this.hiddenOrganizationsUnreadTotal();
    if (unreadCount <= 0) return label;
    const unreadLabel = String(
      this.translate.instant('CHAT.SIDEBAR_UNREAD_BADGE_ARIA', {
        count: unreadCount,
      }),
    );
    return `${label}. ${unreadLabel}`;
  }

  protected switchOrganization(orgId: string): void {
    if (this.switchInProgress()) return;
    this.closeSelector();
    if (orgId === this.organization()?._id) return;

    this.store.dispatch(
      AuthActions.switchOrganizationRequested({ organizationId: orgId }),
    );
  }

  @HostListener('document:keydown.escape')
  protected closeOnEscape(): void {
    this.closeSelector();
  }

  @HostListener('document:click', ['$event'])
  protected closeOnOutsideClick(event: MouseEvent): void {
    if (!this.selectorOpen()) return;

    const drawer =
      this.elementRef.nativeElement.closest('app-sidebar') ??
      this.elementRef.nativeElement;
    if (event.target instanceof Node && drawer.contains(event.target)) return;

    this.closeSelector();
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private updateHiddenOrganizations(): void {
    if (!this.organizationList || this.selectorOpen()) {
      this.hasHiddenOrganizations.set(false);
      this.visibleCompactOrganizations.set(Number.MAX_SAFE_INTEGER);
      return;
    }

    const shortcuts = Array.from(
      this.organizationList.querySelectorAll<HTMLElement>(
        '.organization-shortcut',
      ),
    );
    const rail = this.organizationRail;
    const railHeight = rail?.clientHeight ?? 0;

    if (shortcuts.length && rail && railHeight > 0) {
      const shortcut =
        shortcuts.find((item) => !item.classList.contains('compact-hidden')) ??
        shortcuts[0];
      const shortcutStyles = getComputedStyle(shortcut);
      const listStyles = getComputedStyle(this.organizationList);
      const shortcutHeight =
        shortcut.getBoundingClientRect().height ||
        shortcut.offsetHeight ||
        Number.parseFloat(shortcutStyles.height);
      const shortcutGap = Number.parseFloat(listStyles.rowGap) || 0;
      const listPadding =
        (Number.parseFloat(listStyles.paddingTop) || 0) +
        (Number.parseFloat(listStyles.paddingBottom) || 0);
      const measuredSearchHeight = rail
        .querySelector<HTMLElement>('.organization-search-trigger')
        ?.getBoundingClientRect().height;
      const searchHeight = this.showOrganizationSearch()
        ? measuredSearchHeight !== undefined && measuredSearchHeight > 0
          ? measuredSearchHeight
          : shortcutHeight
        : 0;
      const expandHeight = shortcutHeight;
      const rowsThatFit = (availableHeight: number): number =>
        Math.max(
          0,
          Math.floor(
            (availableHeight - listPadding + shortcutGap) /
              (shortcutHeight + shortcutGap),
          ),
        );
      const rowsWithoutExpand = rowsThatFit(railHeight - searchHeight);

      if (shortcuts.length <= rowsWithoutExpand) {
        this.hasHiddenOrganizations.set(false);
        this.visibleCompactOrganizations.set(Number.MAX_SAFE_INTEGER);
        return;
      }

      this.hasHiddenOrganizations.set(true);
      this.visibleCompactOrganizations.set(
        rowsThatFit(railHeight - searchHeight - expandHeight),
      );
      return;
    }

    const hasOverflow =
      this.organizationList.scrollHeight > this.organizationList.clientHeight;
    if (!hasOverflow) {
      this.hasHiddenOrganizations.set(false);
      this.visibleCompactOrganizations.set(Number.MAX_SAFE_INTEGER);
      return;
    }

    const listBounds = this.organizationList.getBoundingClientRect();
    const hasBrowserLayout = listBounds.height > 0;
    const visibleCount = shortcuts.filter((shortcut) => {
      if (hasBrowserLayout) {
        return shortcut.getBoundingClientRect().bottom <= listBounds.bottom;
      }

      return (
        shortcut.offsetTop + shortcut.offsetHeight <=
        this.organizationList!.clientHeight
      );
    }).length;

    this.hasHiddenOrganizations.set(true);
    this.visibleCompactOrganizations.set(visibleCount);
  }
}
